import { AccessibleCompanySummary, OrganizationType } from '@upright/common/iam/organizations/organization-types';
import { ModelEntityType } from '@upright/common/model/input/types';
import { buildChatUrl } from '@upright/ui/chat/chat-url';

/**
 * Path of the climate-trial campaign link (PLT-6148). Handed out as a bare URL
 * in outbound material, so it must stay stable once published.
 */
export const CLIMATE_TRIAL_PATH = '/go/climate-trial';

/**
 * First message auto-sent on the reader's behalf when the link carries no
 * `?prompt=` of its own. Written in the first person because it renders as the
 * reader's own chat bubble.
 */
export const CLIMATE_TRIAL_PROMPT =
  "How can I test Upright's physical climate risk assessment on any company or location?";

/**
 * Marks the `/dashboard` arrival that should open the "create a trial company"
 * modal. Read and consumed by `ConsultantDashboardView`.
 */
export const DASHBOARD_MODAL_PARAM = 'modal';
export const CLIMATE_TRIAL_MODAL = 'climate-trial';

/**
 * Analytics tag carried by every chat destination this resolver builds, so the
 * first message — which the platform submits on the reader's behalf, not the
 * reader — is logged as a campaign arrival rather than as typed input.
 *
 * Without it the row falls back to the `user_input` default, which both
 * inflates typed-engagement figures and leaves campaign threads unfindable in
 * `chat_message_log`. Matching on the prompt text is not a substitute: the link
 * supports a `?prompt=` override, so the text is not fixed.
 *
 * One value covers every campaign link rather than one per campaign, because
 * each new value has to be added to the backend allow-list in
 * `chat/payload-validation.ts` as well; which campaign a row came from stays
 * derivable from its message and entity binding.
 */
const CAMPAIGN_SOURCE = 'campaign_link';

export interface ClimateTrialAudience {
  /** Type of the viewer's organization; `undefined` when it cannot be resolved. */
  organizationType: OrganizationType | undefined;
  /**
   * The company the organization itself is built around, when it has one. For a
   * single-company org (DMA Freemium) this is the authoritative answer to "which
   * company is theirs" — `companies` comes back from an unordered query, so its
   * first element is not a stable choice.
   */
  organizationCompanyId: string | undefined;
  /**
   * Companies the viewer can actually reach. Only consulted for the two PLG org
   * types; every other audience gets unscoped chat.
   */
  companies: AccessibleCompanySummary[];
}

/**
 * The company a DMA Consultant should test climate risk on, or `null` when
 * there is nothing sensible to pick.
 *
 * A consultant's companies are not equal: a trial run against a client's
 * company spends that client's allotment, so the one to aim at is the
 * consultant's own free slot.
 *
 * `not_required` is the closest signal available on this response, and it is an
 * approximation, not the definition. `company-payment-store.ts` stamps the
 * oldest company in the org `not_required` — the unpaid Starter slot — but if
 * that company has since been upgraded it is stamped `completed` instead, and
 * then nothing here distinguishes it from a client's. Such a consultant falls
 * through to the `/dashboard` dialog even though they are a paying customer;
 * the dashboard lists their companies beside it, so they can still get where
 * they are going, but the dialog is the wrong thing to say to them. Fixing it
 * properly needs the org's own company on this payload (PLT-6148 follow-up).
 */
export function pickConsultantTrialCompany(companies: AccessibleCompanySummary[]): AccessibleCompanySummary | null {
  const ownTrialCompany = companies.find(company => company.paymentStatus === 'not_required');
  if (ownTrialCompany !== undefined) return ownTrialCompany;

  // An end-client user inside a consultant org reaches exactly one company —
  // theirs. There is nothing to choose between, and they typically cannot
  // create companies at all, so sending them to their own company beats
  // sending them to a modal telling them to create a trial.
  if (companies.length === 1) return companies[0];

  return null;
}

/**
 * Where a reader of the climate-trial campaign link should land.
 *
 * Resolved at click time rather than baked into the link, because the audience
 * is mixed and only the platform knows which of them the reader is. Consultant
 * chat is company-scoped server-side — a turn from a multi-company consultant
 * that carries no company is refused — so for that audience the destination has
 * to name a company or the auto-sent prompt dies on arrival.
 */
export function resolveClimateTrialDestination(audience: ClimateTrialAudience, prompt: string): string {
  const { organizationType, organizationCompanyId, companies } = audience;

  if (organizationType === OrganizationType.DMA_CONSULTANT) {
    const company = pickConsultantTrialCompany(companies);
    if (company === null) {
      return `/dashboard?${DASHBOARD_MODAL_PARAM}=${CLIMATE_TRIAL_MODAL}`;
    }
    return chatUrlForCompany(company, prompt);
  }

  if (organizationType === OrganizationType.DMA_FREEMIUM) {
    // Bind to the org's own company, which matches what the dashboard's "Ask
    // AI" button builds and gives the conversation the reader's own company as
    // context. Prefer the org's declared company over the first entry of
    // `companies`: that list arrives unordered, so on the rare freemium org
    // that can reach more than one company its first element is arbitrary.
    const ownCompany =
      companies.find(company => company.companyId === organizationCompanyId) ??
      // Guarded on `length` rather than comparing `companies[0]` to undefined:
      // indexed access is not `| undefined` under this tsconfig, so that
      // comparison is a type-level no-op the linter rejects — while an empty
      // array is a real runtime case (a brand-new org mid-onboarding).
      (companies.length === 0 ? undefined : companies[0]);
    return ownCompany === undefined
      ? buildChatUrl({ from: 'dma', prompt, source: CAMPAIGN_SOURCE })
      : chatUrlForCompany(ownCompany, prompt);
  }

  // Investors, corporate customers and internal users are not company-scoped in
  // chat, so they go straight to the unscoped surface.
  return buildChatUrl({ prompt, source: CAMPAIGN_SOURCE });
}

function chatUrlForCompany(company: AccessibleCompanySummary, prompt: string): string {
  return buildChatUrl({
    from: 'dma',
    prompt,
    entityId: company.companyId,
    entityType: ModelEntityType.COMPANY,
    entityLabel: company.companyName,
    source: CAMPAIGN_SOURCE,
  });
}
